import { useTranslation } from 'react-i18next'
import { writeNegotiationContext } from '../../hooks/useCustomXmlParts'

interface NegotiationSummary {
  id: number
  title: string
  current_version: number
  ready_count: number
  total_parties: number
  sub_status?: string
  deadline?: string | null
  owner?: { koblink_id?: string } | null
}

interface NegotiationPickListProps {
  pickList: NegotiationSummary[]
  onSelect: (negotiationId: number, version: number, title: string) => void
}

/** Badge colors per sub_status value. */
const SUB_STATUS_STYLE: Record<string, { text: string; bg: string }> = {
  IN_REVIEW:         { text: 'text-red-700 dark:text-red-300',     bg: 'bg-red-100 dark:bg-red-900/30' },
  ISSUES_OPEN:       { text: 'text-amber-700 dark:text-amber-300', bg: 'bg-amber-100 dark:bg-amber-900/30' },
  DRAFT_SENT:        { text: 'text-sky-700 dark:text-sky-300',     bg: 'bg-sky-100 dark:bg-sky-900/30' },
  COUNTER_SENT:      { text: 'text-sky-700 dark:text-sky-300',     bg: 'bg-sky-100 dark:bg-sky-900/30' },
  STALLED:           { text: 'text-neutral-600 dark:text-neutral-400', bg: 'bg-neutral-100 dark:bg-neutral-800' },
  AWAITING_PARTIES:  { text: 'text-green-700 dark:text-green-300', bg: 'bg-green-100 dark:bg-green-900/30' },
  READY_FOR_SIGNING: { text: 'text-green-700 dark:text-green-300', bg: 'bg-green-100 dark:bg-green-900/30' },
  PAUSED_BY_OWNER:   { text: 'text-neutral-600 dark:text-neutral-400', bg: 'bg-neutral-100 dark:bg-neutral-800' },
}

const FALLBACK_STYLE = { text: 'text-neutral-600 dark:text-neutral-400', bg: 'bg-neutral-100 dark:bg-neutral-800' }

export function NegotiationPickList({ pickList, onSelect }: NegotiationPickListProps) {
  const { t } = useTranslation()

  return (
    <div className="flex-1 flex flex-col overflow-hidden animate-fade-in">
      <div className="px-3 py-3 space-y-2">
        <p className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
          {t('negotiate.pickTitle', 'This document may belong to an active negotiation:')}
        </p>
        {pickList.map((neg) => {
          const subStyle = neg.sub_status
            ? (SUB_STATUS_STYLE[neg.sub_status] ?? FALLBACK_STYLE)
            : null

          return (
            <button
              key={neg.id}
              onClick={() => {
                onSelect(neg.id, neg.current_version, neg.title)
                writeNegotiationContext({
                  negotiation_id: neg.id,
                  current_version: neg.current_version,
                  title: neg.title,
                  owner_actor_id: neg.owner?.koblink_id,
                }).catch(() => {})
              }}
              className="w-full text-left px-3 py-2.5 rounded-lg border border-neutral-200 dark:border-neutral-700 hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
            >
              <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100 truncate">{neg.title}</p>
              <p className="text-xs text-neutral-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                <span>
                  v{neg.current_version} · {neg.ready_count}/{neg.total_parties} {t('negotiate.ready', 'ready')}
                  {neg.deadline && ` · ${new Date(neg.deadline).toLocaleDateString()}`}
                </span>
                {subStyle && neg.sub_status && (
                  <span className={`inline-block text-[10px] font-medium leading-tight px-1.5 py-0.5 rounded ${subStyle.bg} ${subStyle.text}`}>
                    {t(`negotiate.subStatus.${neg.sub_status}`, neg.sub_status.replace(/_/g, ' '))}
                  </span>
                )}
              </p>
            </button>
          )
        })}
      </div>
    </div>
  )
}
